import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { jewelryDatabase, collectionsDatabase } from '../data/jewelryData.ts';

const Home: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const carouselRef = useRef<HTMLDivElement>(null);
  const touchStartX = useRef<number>(0);
  const touchEndX = useRef<number>(0);

  // Combinar joias e coleções para a seção de novidades
  const allItems = [
    ...jewelryDatabase.map(item => ({ ...item, type: 'jewelry' as const })),
    ...collectionsDatabase.map(item => ({ ...item, type: 'collection' as const }))
  ];

  // Ordenar por ordem (mais recente primeiro) e pegar os primeiros 9
  const featuredItems = allItems
    .sort((a, b) => b.order - a.order)
    .slice(0, 9);

  // Dividir em grupos baseado no tamanho da tela
  const itemsPerGroup = isMobile ? 1 : 3;
  const itemGroups = [];
  for (let i = 0; i < featuredItems.length; i += itemsPerGroup) {
    itemGroups.push(featuredItems.slice(i, i + itemsPerGroup));
  }

  // Atualizar estado mobile quando a tela redimensionar
  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % itemGroups.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + itemGroups.length) % itemGroups.length);
  };

  const getItemLink = (item: any) => {
    return item.type === 'jewelry' ? `/joia/${item.id}` : `/colecao/${item.id}`;
  };

  // Funções para manipular gestos touch
  const handleTouchStart = (e: React.TouchEvent) => {
    if (!isMobile) return;
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isMobile) return;
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (!isMobile) return;
    
    const touchDiff = touchStartX.current - touchEndX.current;
    const minSwipeDistance = 50; // Distância mínima para considerar um swipe
    
    if (Math.abs(touchDiff) > minSwipeDistance) {
      if (touchDiff > 0) {
        // Swipe para a esquerda - próximo slide
        nextSlide();
      } else {
        // Swipe para a direita - slide anterior
        prevSlide();
      }
    }
  };

  return (
    <div>
      {/* Hero Section */}
      <section className="relative h-screen">
        <div className="absolute inset-0">
          <img 
            src={process.env.PUBLIC_URL + '/images/new/cover.jpeg'} 
            alt="Kronara Joias" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        </div>
        <div className="relative h-full flex items-center justify-center text-center px-4">
          <div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              O Ciclo Eterno da Elegância
            </h1>
            <p className="text-xl text-white mb-8 max-w-3xl mx-auto">
              Descubra joias que transcendem o tempo, onde cada peça conta uma história de eternidade e renovação.
            </p>
            <Link to="/catalogo" className="btn btn-primary">
              Explorar Catálogo
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Section with Carousel */}
      <section className="py-16">
        <div className="container">
          <h2 className="section-title">Novidades</h2>
          
          {itemGroups.length > 0 && (
            <div className="relative">
              {/* Carrossel Container */}
              <div 
                className="overflow-hidden"
                ref={carouselRef}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
              >
                <div 
                  className="flex transition-transform duration-500 ease-in-out"
                  style={{ transform: `translateX(-${currentSlide * 100}%)` }}
                >
                  {itemGroups.map((group, groupIndex) => (
                    <div key={groupIndex} className="w-full flex-shrink-0">
                      <div className={`grid gap-8 ${isMobile ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'}`}>
                        {group.map((item) => (
                          <Link
                            key={item.id}
                            to={getItemLink(item)}
                            className="group bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2"
                          >
                            <div className="aspect-square overflow-hidden">
                              <img
                                src={item.images[0]}
                                alt={item.name}
                                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                              />
                            </div>
                            <div className="p-6 text-center">
                              <h3 className="text-xl font-bold text-azul-majestoso group-hover:text-azul-mitologico transition-colors duration-200">
                                {item.name}
                              </h3>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Navegação do Carrossel */}
              {itemGroups.length > 1 && (
                <>
                  <button
                    onClick={prevSlide}
                    className="absolute left-4 top-1/2 -translate-y-1/2 bg-azul-majestoso text-white p-3 rounded-full shadow-lg hover:bg-azul-mitologico transition-colors duration-200 z-10"
                    aria-label="Slide anterior"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <button
                    onClick={nextSlide}
                    className="absolute right-4 top-1/2 -translate-y-1/2 bg-azul-majestoso text-white p-3 rounded-full shadow-lg hover:bg-azul-mitologico transition-colors duration-200 z-10"
                    aria-label="Próximo slide"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                  </button>

                  {/* Indicadores de slide */}
                  <div className="flex justify-center mt-8 space-x-2">
                    {itemGroups.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentSlide(index)}
                        className={`w-3 h-3 rounded-full transition-colors duration-200 ${
                          currentSlide === index ? 'bg-azul-majestoso' : 'bg-gray-300 hover:bg-gray-400'
                        }`}
                        aria-label={`Ir para slide ${index + 1}`}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Home;

